import dayjs from "dayjs";
import { Typography, message, Form, Flex, Button, Input, Avatar, Empty } from "antd";
import CroppedTimelineProps, { MutationKey } from "components/CroppedTimeline";
import { TbChevronUp, TbChevronDown } from "react-icons/tb";
import { UserSchemaDto } from "dtos/user.dto";
import { pickColorFromSeed } from "utils/colors";
import { getInitials, ObjectId } from "utils/functions";
import { ApiService } from "api/api.service";
import { ComponentProps, useMemo } from "react";
import { useAppSelector } from "store";
import { ElementOf, Merge } from "types/_utils.types";
import { CaseSchemaDto } from "dtos/leadCases.dto";

const UserTitleSection = (user?: Partial<Pick<UserSchemaDto, "_id" | "name" | "email" | "avatar_url">>) => {
  return (
    <Flex gap={8} align="center" key={`user_${user?._id}`}>
      <Avatar
        src={user?.avatar_url}
        size={28}
        style={{
          background: pickColorFromSeed(user?.name || "Unknown"),
        }}
      >
        {getInitials(user?.name, 2) ?? "UN"}
      </Avatar>
      <Flex vertical>
        <Typography.Text className="!text-sm">{user?.name}</Typography.Text>
        <Typography.Text type="secondary" className="!text-xs">
          {user?.email}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

type PopulatedUser = Pick<UserSchemaDto, "_id" | "name" | "email" | "avatar_url">;

interface SidebarProps {
  caseId: CaseSchemaDto["_id"];
  logs: Merge<ElementOf<CaseSchemaDto["logs"]>, { user?: PopulatedUser }>[];
  remarks: Merge<ElementOf<CaseSchemaDto["remarks"]>, { user?: PopulatedUser }>[];
  activeSection: "logs" | "remarks";
  logMutationKey: MutationKey;
  setLogMutationKey: (param: MutationKey | ((prev: MutationKey) => MutationKey)) => void;
  remarksMutationKey: MutationKey;
  setRemarksMutationKey: (param: MutationKey | ((prev: MutationKey) => MutationKey)) => void;
  refetch?: () => void;
}

export const Sidebar = ({
  caseId,
  logs,
  remarks,
  activeSection,
  logMutationKey,
  setLogMutationKey,
  remarksMutationKey,
  setRemarksMutationKey,
  refetch,
}: SidebarProps) => {
  const addManualLog = ApiService.lead.useAddLogEntry();
  const addRemark = ApiService.lead.useAddRemark();

  const user = useAppSelector((state) => state.user);
  const userRemarks = useMemo(() => {
    const userId = new ObjectId(user._id ?? "");
    return remarks?.find((rm) => userId.equals(rm.user));
  }, [remarks, user._id]);

  return (
    <div className="bg-gray-100 p-5 !w-1/3 overflow-y-auto hide-scrollbar">
      {activeSection === "logs" ? (
        <>
          <CroppedTimelineProps
            key="logs"
            rootClassName="mb-5"
            data={logs.map((val) => ({ ...val, key: val.user?._id }))}
            itemRenderer={(item) => ({
              color: item.entry_type === "manual" ? "gray" : undefined,
              children: (
                <div className="flex flex-col gap-1">
                  <UserTitleSection {...item?.user} />
                  <Typography.Text className="mt-1">{item?.message ?? item?.step}</Typography.Text>
                  <Typography.Text type="secondary" className="!text-xs">
                    {dayjs(item.date).format("DD MMM YYYY, hh:mma")}
                  </Typography.Text>
                </div>
              ),
            })}
            maxCapacity={5}
            windowSize={3}
            beforeButtonProps={{ icon: <TbChevronUp /> }}
            afterButtonProps={{ icon: <TbChevronDown /> }}
            mutationProps={
              logMutationKey
                ? {
                    mutationKey: logMutationKey,
                    onCancel() {
                      setLogMutationKey(false);
                    },
                    onConfirm(item) {
                      if (!item.message?.trim().length) {
                        return message.warning("Empty log message can't be submitted");
                      }

                      addManualLog.mutate(
                        { id: caseId, message: item.message },
                        {
                          onSuccess() {
                            message.success("Added a new log");
                            refetch?.();
                            setLogMutationKey(false);
                          },
                        },
                      );
                    },
                    renderer(item, { onCancel, onConfirm }) {
                      return {
                        color: "green",
                        pending: true,
                        children: (
                          <EditableField<NonNullable<typeof item> & { key: string | undefined }>
                            user={user}
                            item={item}
                            onCancel={onCancel}
                            onConfirm={onConfirm}
                          />
                        ),
                      };
                    },
                  }
                : undefined
            }
          />
          {!logs.length && !logMutationKey && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Logs" />}
        </>
      ) : (
        <>
          <CroppedTimelineProps
            key="remarks"
            rootClassName="mb-5"
            data={remarks.map((val) => ({ ...val, key: val.user?._id }))}
            itemRenderer={(item) => ({
              children: (
                <div className="flex flex-col gap-1">
                  <UserTitleSection {...item?.user} />
                  <Typography.Text className="mt-1">{item?.message}</Typography.Text>
                  <Typography.Text type="secondary" className="!text-xs">
                    {dayjs(item.date).format("DD MMM YYYY, hh:mma")}
                  </Typography.Text>
                </div>
              ),
            })}
            maxCapacity={5}
            windowSize={3}
            beforeButtonProps={{ icon: <TbChevronUp /> }}
            afterButtonProps={{ icon: <TbChevronDown /> }}
            mutationProps={
              remarksMutationKey
                ? {
                    mutationKey: remarksMutationKey,
                    onCancel() {
                      setRemarksMutationKey(false);
                    },
                    onConfirm(item) {
                      if (!item.message?.trim().length) {
                        return message.warning("Empty remarks can't be submitted");
                      }

                      addRemark.mutate(
                        { id: caseId, message: item.message },
                        {
                          onSuccess() {
                            message.success(userRemarks ? "Updated your remark" : "Added remark");
                            refetch?.();
                            setRemarksMutationKey(false);
                          },
                        },
                      );
                    },
                    renderer(item, { onCancel, onConfirm }) {
                      return {
                        color: "green",
                        pending: true,
                        children: (
                          <EditableField<NonNullable<typeof item> & { key: string | undefined }>
                            user={user}
                            item={item}
                            onCancel={onCancel}
                            onConfirm={onConfirm}
                          />
                        ),
                      };
                    },
                  }
                : undefined
            }
          />
          {!remarks.length && !remarksMutationKey && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Remarks" />
          )}
        </>
      )}
      <Button
        className="w-full"
        variant={
          activeSection === "logs"
            ? logMutationKey === false
              ? "solid"
              : "outlined"
            : remarksMutationKey === false
              ? "solid"
              : "outlined"
        }
        color="primary"
        onClick={() => {
          if (activeSection === "logs") setLogMutationKey((prev) => !prev);
          else if (activeSection === "remarks")
            setRemarksMutationKey((prev) =>
              prev === false ? (userRemarks?.user ? userRemarks.user._id : true) : false,
            );
        }}
      >
        {activeSection === "logs"
          ? logMutationKey !== false
            ? "Cancel"
            : "Add"
          : remarksMutationKey !== false
            ? "Cancel"
            : userRemarks
              ? "Update"
              : "Add"}{" "}
        {activeSection === "logs" ? "Log Entry" : "Remarks"}
      </Button>
    </div>
  );
};

function EditableField<T extends { key: number | string | undefined }>({
  user,
  onCancel,
  onConfirm,
  item,
}: {
  user: ComponentProps<typeof UserTitleSection>;
  item: Parameters<NonNullable<CroppedTimelineProps<T>["mutationProps"]>["renderer"]>[0];
} & Pick<NonNullable<CroppedTimelineProps<T>["mutationProps"]>, "onCancel" | "onConfirm">) {
  return (
    <div className="flex flex-col gap-1">
      <UserTitleSection {...user} />
      <Form onFinish={onConfirm} initialValues={item}>
        <Form.Item name="message" className="my-2">
          <Input.TextArea rows={2} className="!resize-none" placeholder="Add Log Entry" />
        </Form.Item>
        <Flex justify="end" gap={4}>
          <Button htmlType="reset" className="flex-1" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="flex-1" size="small" variant="solid" color="primary" htmlType="submit">
            Confirm
          </Button>
        </Flex>
      </Form>
    </div>
  );
}
